import React, { ReactNode, SetStateAction, useEffect, useState } from 'react';
import { RiArrowDownSLine, RiArrowRightDownLine, RiCloseLine } from 'react-icons/ri';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import * as Styled from './styles';
import { BiGridHorizontal } from 'react-icons/bi';
import { useTranslation } from 'react-i18next';
import { ResizableBox } from 'react-resizable';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';

interface ILayerOption {
  title: ReactNode;
  content: ReactNode;
  setIndex?: React.Dispatch<SetStateAction<number>>;
  index: number;
  pages: number;
  increaseIndex?: () => void;
  onClose: () => void;
  reset?: boolean;
  zoom?: boolean;
  resizable?: boolean;
  defaultSize?: ISize;
  pageable?: boolean;
  defaultPos: IPosition;
}

interface ISize {
  width: number;
  height: number;
}

interface IPosition {
  x: number;
  y: number;
}

export function LayerOption(
  {
    defaultPos,
    title,
    content,
    setIndex,
    index,
    pages,
    increaseIndex,
    onClose,
    reset,
    defaultSize = {width: 250, height: 300},
    zoom = true,
    resizable = true,
    pageable = false,
  }: ILayerOption) {
  const [isActived, setIsActived] = useState<boolean>(true);
  const { t: translation } = useTranslation();
  const [fontSize, setFontSize] = useState<number>(1.2);
  const [position, setPosition] = useState<IPosition>(defaultPos);
  const [size, setSize] = useState<ISize>(defaultSize);

  useEffect(() => {
    setSize(defaultSize);
  }, [defaultSize.width, defaultSize.height])

  useEffect(() => {
    if (innerWidth < 400) {
      setIsActived(false);
    }
  }, []);

  const handleClose = () => {
    onClose();

    if (setIndex) setIndex(0);
  };

  const handlePrevious = () => {
    if (!setIndex) return;
    if (index === 0) {
      setIndex(pages - 1);
    } else {
      setIndex(index - 1);
    }
  };

  const handleNext = () => {
    if (!setIndex) return;
    if (index === pages - 1) {
      setIndex(0);
    } else {
      setIndex(index + 1);
    }
  };

  function increaseZoom() {
    setFontSize(fontSize + 0.1);
  }

  function decreaseZoom() {
    setFontSize(fontSize - 0.1);
  }

  const handleDrag = (e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y });
  };

  const handleActivate = () => {
    if (increaseIndex) increaseIndex();
  };

  useEffect(() => {
    setPosition(defaultPos);
    setSize(defaultSize);
    setIsActived(true)
  }, [reset]);

  return (
    <Styled.Box>
      <Styled.GlobalStyles />
      <Draggable
        bounds='parent'
        handle="#btn-subtitle, #btn-dragglable, #title-dragglable, #header-draggable, #bottom-draggable"
        position={position}
        onDrag={handleDrag}
        onStart={increaseIndex}
      >
        <ResizableBox
          width={size.width}
          height={isActived? size.height : 49}
          minConstraints={[200, 300]}
          maxConstraints={[window.innerWidth - position.x, window.innerHeight - position.y]}
          resizeHandles={isActived && resizable ? ['se'] : []}
          onResize={(e, data) => {
            setSize({
              width: data.size.width,
              height: data.size.height
            });
          }}
          handle={(h, ref) => (
            <span className={`react-resizable-handle react-resizable-handle-${h}`} ref={ref}>
              <RiArrowRightDownLine className="resize-handle-icon" />
            </span>
          )}
        >
          <Styled.ResizableContainer>
            <Styled.LayerBlock
              isActived={isActived}
              id="header-draggable"
              onMouseDown={handleActivate}
              onTouchStart={handleActivate}
            >
              {title}

              <Styled.Button id="btn-dragglable" label={translation('mapPage.modalInformations.dragglable')}>
                <BiGridHorizontal size={24} />
              </Styled.Button>

              <Styled.Button
                id="btn-minimize"
                onClick={() => setIsActived(!isActived)}
                onTouchStart={() => setIsActived(!isActived)}
                label={
                  isActived
                    ? translation('mapPage.modalInformations.minimize')
                    : translation('mapPage.modalInformations.maximize')
                }
              >
                <RiArrowDownSLine id="icon-minimize" size={32} />
              </Styled.Button>

              <Styled.Button
                id="btn-close"
                onClick={handleClose}
                onTouchStart={handleClose}
                label={translation('mapPage.modalInformations.close')}
              >
                <RiCloseLine size={28} />
              </Styled.Button>
            </Styled.LayerBlock>
            <Styled.ContentWrapper isActived={isActived}>
              <Styled.Content
                fontContent={`${fontSize}rem`}
                fontTitle={`${fontSize * 1.2}rem`}
                sizeImage={fontSize - 0.2}
                onMouseDown={handleActivate}
                onTouchStart={handleActivate}
              >
                {content}
                {zoom &&
                  <div className="zoom">
                    <button className="zoomButton" onClick={increaseZoom}>
                      +
                    </button>
                    <button className="zoomButton" onClick={decreaseZoom}>
                      -
                    </button>
                  </div>
                }
              </Styled.Content>
              <Styled.Footer id="bottom-draggable" onMouseDown={handleActivate} onTouchStart={handleActivate}>
                {pageable && (
                  <>
                    <Styled.Button
                      id="btn-previous"
                      onClick={handlePrevious}
                      onTouchStart={handlePrevious}
                      label={translation('mapPage.modalInformations.previous')}
                    >
                      <MdNavigateBefore />
                    </Styled.Button>
                    <Styled.Button
                      id="btn-next"
                      onClick={handleNext}
                      onTouchStart={handleNext}
                      label={translation('mapPage.modalInformations.next')}
                    >
                      <MdNavigateNext />
                    </Styled.Button>
                    <div className="counter">
                      ({index + 1} {translation('mapPage.modalInformations.pageCounter')} {pages})
                    </div>
                  </>
                )}
              </Styled.Footer>
            </Styled.ContentWrapper>
          </Styled.ResizableContainer>
        </ResizableBox>
      </Draggable>
    </Styled.Box>
  );
}
